<template>
  <transition v-if="visible" name="fade">
    <v-sheet id="search-results">
      <div class="container">
        <div class="search-input">
          <input
            v-model="searchTerms"
            ref="searchInput"
            type="text"
            name="search"
            autocomplete="off"
            data-private
            :placeholder="$t('common.search')"
            :disabled="disabled"
            @keydown.esc="handleClose"
          />

          <div class="search-results-close" @click="handleClose">
            <span class="material-icons font-size-20 font-bold"> close </span>
          </div>
        </div>

        <div v-if="searchTerms" class="row">
          <div class="col-md-4">
            <div class="search-results-title">
              <h2>{{ $t('common.rev_other') }}</h2>
            </div>
            <div class="search-results-counter">
              <p>
                <span>{{ revs.length }}</span> {{ $t('common.result_other') }}
              </p>
            </div>

            <LoaderSpinner v-show="revsLoading" />

            <ul v-show="!revsLoading" class="search-results-data">
              <li
                class="search-result"
                v-for="rev in revs"
                :key="rev.id"
                @click="
                  goToRoute({
                    name: rev.type,
                    params: {
                      organizationId: rev.organization._id,
                      revId: rev.id,
                    },
                    rev: rev,
                  })
                "
              >
                <h3 :title="rev.name">
                  {{ rev.name }}
                </h3>
                <div class="search-result-meta">
                  <p v-if="rev.owner.displayname">
                    {{ rev.owner.displayname }}
                    {{ rev.owner.familyname }}
                  </p>
                  <p v-if="!rev.owner.displayname">{{ rev.owner.email }}</p>
                  <p>{{ toHumanDate(rev.createdAt) }}</p>
                </div>
              </li>
            </ul>
          </div>

          <div class="col-md-4">
            <div class="search-results-title">
              <h2>{{ $t('common.users.user_other') }}</h2>
            </div>
            <div class="search-results-counter">
              <p>
                <span>{{ users.length }}</span> {{ $t('common.result_other') }}
              </p>
            </div>

            <LoaderSpinner v-show="usersLoading" />

            <ul v-show="!usersLoading" class="search-results-data">
              <li
                class="search-result"
                v-for="user in users"
                :key="user._id"
                @click="
                  goToRoute({
                    name: 'profile',
                    params: {
                      organizationId: user.organization._id,
                      userId: user._id,
                    },
                  })
                "
              >
                <h3 class="mb-1">{{ getFullName(user) }}</h3>
                <div v-if="user.isDeleted">
                  <v-chip small color="grey lighten-4">
                    {{ $t('people.status.deactivated') }}
                  </v-chip>
                </div>
              </li>
            </ul>
          </div>

          <div class="col-md-4">
            <div class="search-results-title">
              <h2>{{ $t('common.challenge_other') }}</h2>
            </div>
            <div class="search-results-counter">
              <p>
                <span>{{ challenges.length }}</span>
                {{ $t('common.result_other') }}
              </p>
            </div>

            <LoaderSpinner v-show="challengesLoading" />

            <ul v-show="!challengesLoading" class="search-results-data">
              <li
                class="search-result"
                v-for="challenge in challenges"
                :key="challenge._id"
                @click="
                  goToRoute({
                    name: 'challenge',
                    params: {
                      organizationId: challenge.organization._id,
                      challengeId: challenge._id,
                    },
                  })
                "
              >
                <h3 :title="challenge.name">{{ challenge.name }}</h3>

                <div class="search-result-meta">
                  <p>
                    {{
                      $t('common.start_at_end_at', {
                        startAt: toHumanDate(challenge.startAt),
                        endAt: toHumanDate(challenge.endAt),
                        interpolation: { escapeValue: false },
                      })
                    }}
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </v-sheet>
  </transition>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { debounce } from 'lodash'
import { utils } from 'frontend-js-shared'

import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import LoaderSpinner from '@/components/utils/LoaderSpinner.vue'

export default {
  name: 'search-results',

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      disabled: false,
      searchTerms: '',
    }
  },

  mixins: [mixin, trackerMixin],

  components: {
    LoaderSpinner,
  },

  watch: {
    searchTerms(newSearchTerm) {
      if (!newSearchTerm.length) {
        return this.clearResults()
      }
      this.fetchResults()
    },

    async visible(isVisible) {
      if (!isVisible) return

      await this.$nextTick()
      this.$refs.searchInput?.focus?.()
    },
  },

  computed: {
    ...mapState({
      revs: state => state.search.revsResults,
      users: state => state.search.usersResults,
      challenges: state => state.search.challengesResults,
      revsLoading: state => state.search.revsLoading,
      usersLoading: state => state.search.usersLoading,
      challengesLoading: state => state.search.challengesLoading,
      isSearchActive: state => state.search.isActive,
    }),
  },

  methods: {
    ...mapActions({
      makeUberSearch: 'makeUberSearch',
    }),

    fetchResults: debounce(function() {
      this.makeUberSearch(this.searchTerms)
      this.$track('app.Search', this.$store, {
        terms: this.searchTerms,
      })
    }, 300),

    handleClose() {
      this.$store.commit('SET_UBER_SEARCH_INACTIVE')
      this.clearResults()
      this.searchTerms = ''
      this.$emit('close')
    },

    goToRoute(route) {
      if (route.name === 'improvementIdea' || route.name === 'problemSolving')
        this.triggerRevViewEvent(route.rev.metadata)
      this.$emit('close')
      this.navigateTo(route)
    },

    toHumanDate(date) {
      return utils.dates.humanDate(date)
    },

    triggerRevViewEvent(rev) {
      this.trackView(rev)
    },

    clearResults() {
      this.$store.commit('CLEAR_REVS_RESULTS')
      this.$store.commit('CLEAR_USERS_RESULTS')
      this.$store.commit('CLEAR_CHALLENGES_RESULTS')
    },
  },
}
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

#search-results {
  background-color: $color-white;
  height: 100%;
  width: 100%;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  padding-top: 35px;
  overflow: auto;
  z-index: 9999999999;
}

.search-input {
  position: relative;
  margin-bottom: 40px;

  &:before {
    content: '';
    display: block;
    position: absolute;
    background-image: url('../../../assets/images/icons/search.png');
    background-repeat: no-repeat;
    background-position: center center;
    background-size: 100%;
    height: 24px;
    width: 24px;
    left: 0;
    top: 10px;
    margin: auto 0;
  }

  input {
    background-color: transparent;
    font-size: 30px;
    border: none;
    border-bottom: 1px solid $color-blue-grey-lighten1;
    width: 100%;
    margin-bottom: 16px;
    padding: 0 48px 8px 48px;
    outline: none;
    color: $color-blue-grey-darken3;
    font-weight: 400;
    transition: all 0.5s;

    &:focus {
      border-bottom: 2px solid $color-blue;
    }

    &::-webkit-input-placeholder,
    &::-moz-placeholder,
    &:-ms-input-placeholder,
    &:-moz-placeholder,
    &::placeholder {
      color: $color-blue-grey-darken1;
      font-weight: 400;
    }
  }
}

.search-results-close {
  position: absolute;
  z-index: 10;
  top: 0;
  right: 0;
  padding: 20px;
  color: $color-blue-grey-darken1;
  background-color: $color-blue-grey-lighten2;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover {
    background-color: $color-blue-grey-lighten1;
  }
}

.search-results-title {
  padding: 10px 0;
  margin-bottom: 10px;
  border-bottom: 1px solid $color-blue-grey-lighten1;
  h2 {
    margin: 0;
    font-size: 12px;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: $color-blue-darken1;
  }
}

.search-results-data {
  padding: 0;
  margin: 0;
  cursor: pointer;
  overflow: auto;
  height: 50vh;
}

.search-results-counter {
  text-align: right;
  font-size: 11px;
  color: $color-blue-grey-darken3;
  margin-bottom: 20px;
  span {
    font-weight: bold;
  }
}

.search-result {
  list-style-type: none;
  padding: 5px;
  border-radius: 4px;
  margin-bottom: 10px;

  &:hover {
    background-color: map-get($grey, lighten-4);
  }

  h3 {
    margin: 0 0 4px 0;
    font-size: 16px;
    color: $color-blue-grey-darken2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  p {
    margin: 0;
  }
  .search-result-meta {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    font-size: 13px;
    color: $color-blue-grey-darken3;
  }
}
</style>
